import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import { CheckCircle, AlertCircle, X } from 'lucide-react';
import { API_ENDPOINTS } from '../../../../utils/config';

const REQUEST_TIMEOUT_MS = 20000;

const SalesCatalogModal = ({ isOpen, onClose, catalogItem, onSave, mode = 'add' }) => {
  const [categoryType, setCategoryType] = useState('goods'); // 'goods' or 'services'
  const [formData, setFormData] = useState({
    goods_data: {
      goods_name: '',
      price: '',
      gst_treatment: 'inclusive of GST',
      gst_rate: '',
      non_taxable: 0,
      net_gst: {
        net_price: '',
        gst_amount: ''
      },
      hsn_code: '',
      units: '',
      cess_amount: '0',
      sku: '',
      stock_quantity: '',
      description: ''
    },
    services_data: {
      service_name: '',
      price: '',
      gst_treatment: 'inclusive of GST',
      gst_rate: '',
      non_taxable: 0,
      net_gst: {
        net_price: '',
        gst_amount: ''
      },
      sac_code: '',
      cess_amount: '0',
      description: ''
    }
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && catalogItem) {
        const type = catalogItem.category_type || 'goods';
        setCategoryType(type);
        
        if (type === 'goods') {
          const goodsData = catalogItem.goods_data || catalogItem.services_data || {};
          setFormData(prev => ({
            ...prev,
            goods_data: {
              goods_name: goodsData.goods_name || '',
              price: goodsData.price || '',
              gst_treatment: goodsData.gst_treatment || 'inclusive of GST',
              gst_rate: goodsData.gst_rate || '',
              non_taxable: goodsData.non_taxable || 0,
              net_gst: {
                net_price: goodsData.net_gst?.net_price || '',
                gst_amount: goodsData.net_gst?.gst_amount || ''
              },
              hsn_code: goodsData.hsn_code || '',
              units: goodsData.units || '',
              cess_amount: goodsData.cess_amount || '0',
              sku: goodsData.sku || '',
              stock_quantity: goodsData.stock_quantity || '',
              description: goodsData.description || ''
            }
          }));
        } else {
          const servicesData = catalogItem.services_data || catalogItem.goods_data || {};
          setFormData(prev => ({
            ...prev,
            services_data: {
              service_name: servicesData.service_name || '',
              price: servicesData.price || '',
              gst_treatment: servicesData.gst_treatment || 'inclusive of GST',
              gst_rate: servicesData.gst_rate || '',
              non_taxable: servicesData.non_taxable || 0,
              net_gst: {
                net_price: servicesData.net_gst?.net_price || '',
                gst_amount: servicesData.net_gst?.gst_amount || ''
              },
              sac_code: servicesData.sac_code || '',
              cess_amount: servicesData.cess_amount || '0',
              description: servicesData.description || ''
            }
          }));
        }
      } else {
        // Reset form for add mode
        setCategoryType('goods');
        setFormData({
          goods_data: {
            goods_name: '',
            price: '',
            gst_treatment: 'inclusive of GST',
            gst_rate: '',
            non_taxable: 0,
            net_gst: { net_price: '', gst_amount: '' },
            hsn_code: '',
            units: '',
            cess_amount: '0',
            sku: '',
            stock_quantity: '',
            description: ''
          },
          services_data: {
            service_name: '',
            price: '',
            gst_treatment: 'inclusive of GST',
            gst_rate: '',
            non_taxable: 0,
            net_gst: { net_price: '', gst_amount: '' },
            sac_code: '',
            cess_amount: '0',
            description: ''
          }
        });
      }
      setErrors({});
      setErrorMessage('');
    }
  }, [catalogItem, mode, isOpen]);

  const handleInputChange = (section, field, value) => {
    if (field.includes('.')) {
      // Handle nested fields like net_gst.net_price
      const [parentField, childField] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [parentField]: {
            ...prev[section][parentField],
            [childField]: value
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    }
    
    // Clear error when user starts typing
    if (errors?.[`${section}.${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${section}.${field}`];
        return newErrors;
      });
    }
  };

  const calculateGST = (price, gstRate, gstTreatment) => {
    if (!price || !gstRate) return { net_price: '', gst_amount: '' };
    
    const priceNum = parseFloat(price);
    const gstRateNum = parseFloat(gstRate.replace('%', ''));
    
    if (gstTreatment === 'inclusive of GST') {
      const netPrice = (priceNum * 100) / (100 + gstRateNum);
      const gstAmount = priceNum - netPrice;
      return {
        net_price: netPrice.toFixed(2),
        gst_amount: gstAmount.toFixed(2)
      };
    } else {
      const gstAmount = (priceNum * gstRateNum) / 100;
      const netPrice = priceNum;
      return {
        net_price: netPrice.toFixed(2),
        gst_amount: gstAmount.toFixed(2)
      };
    }
  };

  useEffect(() => {
    const currentData = categoryType === 'goods' ? formData.goods_data : formData.services_data;
    if (currentData.price && currentData.gst_rate) {
      const calculated = calculateGST(currentData.price, currentData.gst_rate, currentData.gst_treatment);
      const dataKey = `${categoryType}_data`;
      setFormData(prev => ({
        ...prev,
        [dataKey]: {
          ...prev[dataKey],
          net_gst: {
            net_price: calculated.net_price,
            gst_amount: calculated.gst_amount
          }
        }
      }));
    }
  }, [categoryType, formData.goods_data.price, formData.goods_data.gst_rate, formData.goods_data.gst_treatment, formData.services_data.price, formData.services_data.gst_rate, formData.services_data.gst_treatment]);

  const validateForm = () => {
    const newErrors = {};
    const currentData = categoryType === 'goods' ? formData.goods_data : formData.services_data;
    const dataKey = categoryType === 'goods' ? 'goods_data' : 'services_data';

    if (categoryType === 'goods') {
      if (!currentData.goods_name?.trim()) {
        newErrors[`${dataKey}.goods_name`] = 'Goods name is required';
      }
      if (!currentData.price?.trim()) {
        newErrors[`${dataKey}.price`] = 'Price is required';
      }
      if (!currentData.gst_rate?.trim()) {
        newErrors[`${dataKey}.gst_rate`] = 'GST rate is required';
      }
      if (!currentData.hsn_code?.trim()) {
        newErrors[`${dataKey}.hsn_code`] = 'HSN code is required';
      }
      if (!currentData.units?.trim()) {
        newErrors[`${dataKey}.units`] = 'Units is required';
      }
      if (!currentData.sku?.trim()) {
        newErrors[`${dataKey}.sku`] = 'SKU is required';
      }
      if (!currentData.stock_quantity?.trim()) {
        newErrors[`${dataKey}.stock_quantity`] = 'Stock quantity is required';
      }
    } else {
      if (!currentData.service_name?.trim()) {
        newErrors[`${dataKey}.service_name`] = 'Service name is required';
      }
      if (!currentData.price?.trim()) {
        newErrors[`${dataKey}.price`] = 'Price is required';
      }
      if (!currentData.gst_rate?.trim()) {
        newErrors[`${dataKey}.gst_rate`] = 'GST rate is required';
      }
      if (!currentData.sac_code?.trim()) {
        newErrors[`${dataKey}.sac_code`] = 'SAC code is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      setErrorMessage('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };
      const axiosConfig = {
        headers,
        timeout: REQUEST_TIMEOUT_MS
      };

      const payload = {
        category_type: categoryType,
        [categoryType === 'goods' ? 'goods_data' : 'services_data']: categoryType === 'goods' 
          ? {
              ...formData.goods_data,
              price: String(formData.goods_data.price),
              stock_quantity: String(formData.goods_data.stock_quantity),
              non_taxable: Number(formData.goods_data.non_taxable) || 0,
              cess_amount: String(formData.goods_data.cess_amount || '0')
            }
          : {
              ...formData.services_data,
              price: String(formData.services_data.price),
              non_taxable: Number(formData.services_data.non_taxable) || 0,
              cess_amount: String(formData.services_data.cess_amount || '0')
            }
      };

      if (mode === 'edit' && catalogItem?.id) {
        await axios.put(
          API_ENDPOINTS.SALES_CATALOG_UPDATE(catalogItem.id),
          payload,
          axiosConfig
        );
      } else {
        await axios.post(
          API_ENDPOINTS.SALES_CATALOG_CREATE,
          payload,
          axiosConfig
        );
      }

      onSave();
      onClose();
    } catch (error) {
      const timeoutTriggered = error?.code === 'ECONNABORTED';
      const apiError =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        (timeoutTriggered
          ? 'Request timed out. Please check your network or try again in a moment.'
          : error?.message) ||
        'Failed to save sales catalog item. Please try again.';
      setErrorMessage(apiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const currentData = categoryType === 'goods' ? formData.goods_data : formData.services_data;
  const dataKey = categoryType === 'goods' ? 'goods_data' : 'services_data';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-gray-900">
              {mode === 'edit' ? 'Edit' : 'Add'} Sales Catalog Item
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {errorMessage && (
            <div className="flex items-start gap-3 p-4 rounded-lg border border-red-200 bg-red-50 text-red-800">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <p className="text-sm">{errorMessage}</p>
            </div>
          )}

          {/* Category Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category Type <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="goods"
                  checked={categoryType === 'goods'}
                  onChange={(e) => setCategoryType(e.target.value)}
                  className="mr-2"
                  disabled={mode === 'edit'}
                />
                <span>Goods</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="services"
                  checked={categoryType === 'services'}
                  onChange={(e) => setCategoryType(e.target.value)}
                  className="mr-2"
                  disabled={mode === 'edit'}
                />
                <span>Services</span>
              </label>
            </div>
          </div>

          {/* Goods Fields */}
          {categoryType === 'goods' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Goods Name"
                  type="text"
                  placeholder="Enter goods name"
                  value={currentData.goods_name}
                  onChange={(e) => handleInputChange(dataKey, 'goods_name', e.target.value)}
                  error={errors[`${dataKey}.goods_name`]}
                  required
                />
                <Input
                  label="Price"
                  type="text"
                  placeholder="Enter price"
                  value={currentData.price}
                  onChange={(e) => handleInputChange(dataKey, 'price', e.target.value)}
                  error={errors[`${dataKey}.price`]}
                  required
                />
                <Input
                  label="GST Treatment"
                  type="text"
                  placeholder="e.g., inclusive of GST"
                  value={currentData.gst_treatment}
                  onChange={(e) => handleInputChange(dataKey, 'gst_treatment', e.target.value)}
                />
                <Input
                  label="GST Rate"
                  type="text"
                  placeholder="e.g., 18%"
                  value={currentData.gst_rate}
                  onChange={(e) => handleInputChange(dataKey, 'gst_rate', e.target.value)}
                  error={errors[`${dataKey}.gst_rate`]}
                  required
                />
                <Input
                  label="HSN Code"
                  type="text"
                  placeholder="Enter HSN code"
                  value={currentData.hsn_code}
                  onChange={(e) => handleInputChange(dataKey, 'hsn_code', e.target.value)}
                  error={errors[`${dataKey}.hsn_code`]}
                  required
                />
                <Input
                  label="Units"
                  type="text"
                  placeholder="e.g., box, kg, piece"
                  value={currentData.units}
                  onChange={(e) => handleInputChange(dataKey, 'units', e.target.value)}
                  error={errors[`${dataKey}.units`]}
                  required
                />
                <Input
                  label="SKU"
                  type="text"
                  placeholder="Enter SKU"
                  value={currentData.sku}
                  onChange={(e) => handleInputChange(dataKey, 'sku', e.target.value)}
                  error={errors[`${dataKey}.sku`]}
                  required
                />
                <Input
                  label="Stock Quantity"
                  type="text"
                  placeholder="Enter stock quantity"
                  value={currentData.stock_quantity}
                  onChange={(e) => handleInputChange(dataKey, 'stock_quantity', e.target.value)}
                  error={errors[`${dataKey}.stock_quantity`]}
                  required
                />
                <Input
                  label="CESS Amount"
                  type="text"
                  placeholder="Enter CESS amount"
                  value={currentData.cess_amount}
                  onChange={(e) => handleInputChange(dataKey, 'cess_amount', e.target.value)}
                />
                <Input
                  label="Non Taxable"
                  type="number"
                  placeholder="0"
                  value={currentData.non_taxable}
                  onChange={(e) => handleInputChange(dataKey, 'non_taxable', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Net Price"
                  type="text"
                  value={currentData.net_gst?.net_price || ''}
                  disabled
                />
                <Input
                  label="GST Amount"
                  type="text"
                  value={currentData.net_gst?.gst_amount || ''}
                  disabled
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  placeholder="Enter description"
                  value={currentData.description}
                  onChange={(e) => handleInputChange(dataKey, 'description', e.target.value)}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Services Fields */}
          {categoryType === 'services' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Service Name"
                  type="text"
                  placeholder="Enter service name"
                  value={currentData.service_name}
                  onChange={(e) => handleInputChange(dataKey, 'service_name', e.target.value)}
                  error={errors[`${dataKey}.service_name`]}
                  required
                />
                <Input
                  label="Price"
                  type="text"
                  placeholder="Enter price"
                  value={currentData.price}
                  onChange={(e) => handleInputChange(dataKey, 'price', e.target.value)}
                  error={errors[`${dataKey}.price`]}
                  required
                />
                <Input
                  label="GST Treatment"
                  type="text"
                  placeholder="e.g., inclusive of GST"
                  value={currentData.gst_treatment}
                  onChange={(e) => handleInputChange(dataKey, 'gst_treatment', e.target.value)}
                />
                <Input
                  label="GST Rate"
                  type="text"
                  placeholder="e.g., 5%"
                  value={currentData.gst_rate}
                  onChange={(e) => handleInputChange(dataKey, 'gst_rate', e.target.value)}
                  error={errors[`${dataKey}.gst_rate`]}
                  required
                />
                <Input
                  label="SAC Code"
                  type="text"
                  placeholder="Enter SAC code"
                  value={currentData.sac_code}
                  onChange={(e) => handleInputChange(dataKey, 'sac_code', e.target.value)}
                  error={errors[`${dataKey}.sac_code`]}
                  required
                />
                <Input
                  label="CESS Amount"
                  type="text"
                  placeholder="Enter CESS amount"
                  value={currentData.cess_amount}
                  onChange={(e) => handleInputChange(dataKey, 'cess_amount', e.target.value)}
                />
                <Input
                  label="Non Taxable"
                  type="number"
                  placeholder="0"
                  value={currentData.non_taxable}
                  onChange={(e) => handleInputChange(dataKey, 'non_taxable', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Net Price"
                  type="text"
                  value={currentData.net_gst?.net_price || ''}
                  disabled
                />
                <Input
                  label="GST Amount"
                  type="text"
                  value={currentData.net_gst?.gst_amount || ''}
                  disabled
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  placeholder="Enter description"
                  value={currentData.description}
                  onChange={(e) => handleInputChange(dataKey, 'description', e.target.value)}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  rows={3}
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalesCatalogModal;

